// /app/frontend/src/lib/labels.js
//
// MESP Platform-Wide Terminology Dictionary (Feb 2026).
//
// Single source of truth for Arabic translations of every internal enum,
// role, action verb, and empty-state message rendered in the UI.
//
// Rules:
//  - Every key MUST be the exact raw enum/role string emitted by the backend.
//  - Arabic values MUST match the wording approved by the product team.
//  - Helpers fall back to the raw value when no translation exists so the
//    UI never renders `undefined`.
//
// Usage:
//   import { tStatus, tRole, tAction, tEmpty } from '../lib/labels';
//   tStatus('Active_Student', language)   // → "طالب نشط" in ar, raw in en
//   tRole('Academic_Supervisor', language) // → "مشرف أكاديمي" in ar, "Academic Supervisor" in en
//

// ---------- Status / lifecycle enums ----------
export const STATUS_AR = {
  // Registration lifecycle
  Pending:                    'قيد المراجعة',
  Under_Review:               'قيد المراجعة',
  Assessment_Required:        'تقييم مطلوب',
  Placement_Required:         'بانتظار التوزيع',
  Placement_Reserved:         'محجوز للتوزيع',
  Level_Approved:             'تم اعتماد المستوى',
  Trial_Student:              'طالب تجريبي',
  Trial_Expired:              'انتهت التجربة',
  Awaiting_Payment:           'بانتظار الدفع',
  Paid_Awaiting_Distribution: 'مدفوع - بانتظار التوزيع',
  Distributed:                'تم التوزيع',
  Active_Student:             'طالب نشط',
  Rejected:                   'مرفوض',
  Approved:                   'موافق عليه',

  // Operational / subscription / trial lifecycle
  Active:        'نشط',
  Inactive:      'غير نشط',
  Frozen:        'مجمد',
  Withdrawn:     'منسحب',
  Completed:     'مكتمل',
  Expired:       'منتهي',
  Suspended:     'موقوف',
  None:          'لا يوجد',
  Not_Requested: 'لم يطلب',
  Converted:     'تم التحويل',
  Pending_Placement: 'بانتظار التوزيع',

  // Attendance
  Present: 'حاضر',
  Absent:  'غائب',
  Late:    'متأخر',
  Partial: 'حضور جزئي',
  Excused: 'معذور',

  // Study type / period
  // 2026-06-28 — unified period labels; DB keys (Morning/Afternoon/
  // Evening/Night) unchanged, only the Arabic display strings.
  Individual: 'فردي',
  Group:      'مجموعة',
  Morning:    'الفترة الصباحية',
  Afternoon:  'الفترة الظهرية',
  Evening:    'الفترة العصرية',
  Night:      'الفترة الليلية',

  // Assignment / submission lifecycle
  Draft:         'مسودة',
  Published:     'منشور',
  Closed:        'مغلق',
  Archived:      'مؤرشف',
  Submitted:     'تم التسليم',
  Graded:        'تم التقييم',
  Returned:      'مُعاد',
  Not_Submitted: 'لم يتم التسليم',

  // Trial label shown in tables (when not a full lifecycle enum)
  Trial: 'تجربة',

  // Registration / applicant types
  Individual_Student:   'حساب طالب مستقل',
  Parent_With_Children: 'حساب عائلي / ولي أمر',

  // Gender
  Male:   'ذكر',
  Female: 'أنثى',

  // Classroom audience category (Smart Suggestion — Feb 2026)
  Children:    'الأطفال',
  Girls_Women: 'البنات والنساء',
  Boys_Men:    'الأولاد والرجال',

  // Weekdays (for classroom `schedule_days` / `study_days`)
  Sunday:    'الأحد',
  Monday:    'الإثنين',
  Tuesday:   'الثلاثاء',
  Wednesday: 'الأربعاء',
  Thursday:  'الخميس',
  Friday:    'الجمعة',
  Saturday:  'السبت',
};

// ---------- Roles ----------
export const ROLE_AR = {
  Super_Admin:          'مدير عام',
  Academic_Supervisor:  'مشرف أكاديمي',
  Registration_Officer: 'موظف تسجيل',
  Financial_Manager:    'مدير مالي',
  Teacher:              'معلّم',
  Student:              'طالب',
  Parent:               'ولي أمر',
};

export const ROLE_EN = {
  Super_Admin:          'Super Admin',
  Academic_Supervisor:  'Academic Supervisor',
  Registration_Officer: 'Registration Officer',
  Financial_Manager:    'Financial Manager',
  Teacher:              'Teacher',
  Student:              'Student',
  Parent:               'Parent',
};

// ---------- Action verbs (button labels) ----------
export const ACTION_AR = {
  edit:     'تعديل',
  delete:   'حذف',
  save:     'حفظ',
  cancel:   'إلغاء',
  submit:   'إرسال',
  approve:  'موافقة',
  reject:   'رفض',
  view:     'عرض',
  add:      'إضافة',
  create:   'إنشاء',
  update:   'تحديث',
  close:    'إغلاق',
  open:     'فتح',
  search:   'بحث',
  filter:   'تصفية',
  reset:    'إعادة تعيين',
  refresh:  'تحديث',
  export:   'تصدير',
  import:   'استيراد',
  back:     'رجوع',
  next:     'التالي',
  previous: 'السابق',
  confirm:  'تأكيد',
  loading:  'جاري التحميل...',
  saving:   'جاري الحفظ...',
};

export const ACTION_EN = {
  edit:     'Edit',
  delete:   'Delete',
  save:     'Save',
  cancel:   'Cancel',
  submit:   'Submit',
  approve:  'Approve',
  reject:   'Reject',
  view:     'View',
  add:      'Add',
  create:   'Create',
  update:   'Update',
  close:    'Close',
  open:     'Open',
  search:   'Search',
  filter:   'Filter',
  reset:    'Reset',
  refresh:  'Refresh',
  export:   'Export',
  import:   'Import',
  back:     'Back',
  next:     'Next',
  previous: 'Previous',
  confirm:  'Confirm',
  loading:  'Loading...',
  saving:   'Saving...',
};

// ---------- Empty state messages ----------
export const EMPTY_AR = {
  no_data:    'لا توجد بيانات',
  no_rows:    'لا توجد سجلات',
  no_results: 'لا توجد نتائج',
  not_found:  'غير موجود',
  loading:    'جاري التحميل...',
};

export const EMPTY_EN = {
  no_data:    'No data',
  no_rows:    'No rows',
  no_results: 'No results',
  not_found:  'Not found',
  loading:    'Loading...',
};

// ---------- Helpers ----------
const isAr = (lang) => lang === 'ar';

// Some lifecycle enums arrive from the backend in mixed casing
// (e.g. "Not_Submitted", "NOT_SUBMITTED", "not_submitted"). Normalise to the
// canonical `Title_Case` so the dictionary stays compact.
const _normalize = (value) => {
  if (typeof value !== 'string' || !value) return value;
  return value
    .toLowerCase()
    .split('_')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join('_');
};

export const tStatus = (value, lang) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return isAr(lang) ? (value ? 'نعم' : 'لا') : (value ? 'Yes' : 'No');
  const normalised = _normalize(value);
  if (isAr(lang) && STATUS_AR[normalised] !== undefined) return STATUS_AR[normalised];
  if (isAr(lang) && STATUS_AR[value] !== undefined) return STATUS_AR[value];
  // Dev-only telemetry: surface unmapped enums so future drift is caught early.
  if (isAr(lang) && typeof value === 'string' && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[i18n] missing STATUS_AR key:', value);
  }
  // English mode: humanize the raw enum (replace underscores).
  if (typeof value === 'string') return value.replace(/_/g, ' ');
  return value;
};

export const tRole = (role, lang) => {
  if (!role) return '—';
  if (isAr(lang)) return ROLE_AR[role] || role.replace(/_/g, ' ');
  return ROLE_EN[role] || role.replace(/_/g, ' ');
};

export const tAction = (key, lang) =>
  isAr(lang) ? (ACTION_AR[key] || key) : (ACTION_EN[key] || key);

export const tEmpty = (key, lang) =>
  isAr(lang) ? (EMPTY_AR[key] || EMPTY_AR.no_data) : (EMPTY_EN[key] || EMPTY_EN.no_data);

// Arabic-safe error sanitizer (Feb 2026 — full-system localization sweep).
// Three layers:
//   1. If the language is English, return the raw detail as-is (or fallback).
//   2. If the language is Arabic AND the raw string already contains Arabic
//      characters, trust the backend's localized message and return it.
//   3. If the language is Arabic but the raw string is English/technical,
//      consult the LEGACY_AR_TRANSLATIONS dictionary for a known phrase.
//      Otherwise fall back to the caller-provided Arabic fallback.
//
// This dictionary catches stale English strings emitted by older endpoints
// while the backend localization sweep rolls out — every entry should be
// removed once the corresponding backend response is itself Arabized.
const LEGACY_AR_TRANSLATIONS = {
  // Auth
  'no account found for that phone number': 'لا يوجد حساب مرتبط برقم الهاتف المُدخل.',
  'no account found for that email': 'لا يوجد حساب مرتبط بالبريد الإلكتروني المُدخل.',
  'incorrect password': 'كلمة المرور غير صحيحة.',
  'current password is incorrect': 'كلمة المرور الحالية غير صحيحة.',
  'current password is required': 'كلمة المرور الحالية مطلوبة.',
  'account is not active': 'تم تعطيل هذا الحساب. يرجى التواصل مع الإدارة.',
  'account disabled': 'تم تعطيل هذا الحساب. يرجى التواصل مع الإدارة.',
  'access denied': 'ليس لديك صلاحية للوصول.',
  'authentication required': 'يرجى تسجيل الدخول للمتابعة.',
  'not authenticated': 'يرجى تسجيل الدخول للمتابعة.',
  'session expired': 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.',
  'token expired': 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.',
  'refresh token expired': 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.',
  'invalid token': 'رمز جلسة غير صالح، يرجى تسجيل الدخول مرة أخرى.',
  'invalid token type': 'رمز جلسة غير صالح، يرجى تسجيل الدخول مرة أخرى.',
  'no refresh token': 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى.',
  'user not found': 'لم يتم العثور على المستخدم.',
  'email already registered': 'هذا البريد الإلكتروني مسجَّل مسبقاً.',
  'email or phone number is required': 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف.',
  // Network / HTTP
  'network error': 'حدث خطأ في الاتصال، يرجى المحاولة مرة أخرى.',
  'request failed': 'فشل تنفيذ الطلب، يرجى المحاولة مرة أخرى.',
  'internal server error': 'حدث خطأ في الخادم، يرجى المحاولة لاحقاً.',
};

export const tSafeError = (rawDetail, arabicFallback, lang) => {
  if (!isAr(lang)) return rawDetail || arabicFallback;
  if (!rawDetail || typeof rawDetail !== 'string') return arabicFallback;
  // Arabic content already present → trust the backend.
  if (/[\u0600-\u06FF]/.test(rawDetail)) return rawDetail;
  // Try the legacy dictionary (case-insensitive, trims trailing dots).
  const key = rawDetail.toLowerCase().replace(/[.\s]+$/, '').trim();
  if (LEGACY_AR_TRANSLATIONS[key]) return LEGACY_AR_TRANSLATIONS[key];
  // Partial-prefix match for messages with variables ("invalid phone number: ...").
  for (const phrase of Object.keys(LEGACY_AR_TRANSLATIONS)) {
    if (key.startsWith(phrase)) return LEGACY_AR_TRANSLATIONS[phrase];
  }
  // Unknown English/technical detail → suppress and show friendly fallback.
  return arabicFallback;
};
